import React, { Fragment } from "react";

import { ReactComponent as DescriptionIcon } from "@common/assets/images/filter-icons/description-icon.svg";
import { ReactComponent as JiraIcon } from "@common/assets/images/integrations/jira-icon.svg";
import { ReactComponent as LinearIcon } from "@common/assets/images/integrations/linear-icon.svg";
import { ReactComponent as NoteIcon } from "@common/assets/images/note-write-icon.svg";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { ToggleButton } from "@common/components/ToggleButton/ToggleButton";
import { AppUserShared, getUserInitials } from "@common/models";
import { CrashActivityType } from "@common/models/crashReporting";
import { resolveCreationTimeString } from "@common/models/helpers/time/creationTime";
import { ActivityType, IssueMessage, IssueMessageType } from "@common/models/issueTracking";
import { TicketPriority } from "@common/models/TicketPriority.model";
import { TicketStatus } from "@common/models/TicketStatus.model";
import { vars } from "@common/styles";
import identifiers from "@common/util/identifiers.json";
import * as Icon from "@main/pages/shared/icons/icons";
import Linkify from "linkify-react";

import ChatMessage from "./ChatMessage";
import * as Styled from "./MessageContainer.styles";
import MessageInput from "../Messages/MessageInput/MessageInput";

interface Props {
  messages?: IssueMessage[];
  appUser?: AppUserShared;
}

export default function MessageContainer({ messages, appUser }: Props) {
  const inputPlaceholder = `Leave an internal note — such as "Is this ready for the 2.1.5 release?" or "Check with the backend team first" — visible only to you and your teammates`;
  return (
    <Styled.MessageContainerWrap>
      {messages && (
        <Fragment>
          {!messages.find((a) => a.type === IssueMessageType.ACTIVITY && a.activity_type === "Rule") &&
            noRulesTriggered()}

          {messages.map((message) => {
            switch (message.type) {
              case IssueMessageType.CHAT:
                if (message.is_note && message.user.name !== "Shake AI") {
                  return (
                    <Styled.SystemMessage
                      key={message.id}
                      style={{ alignItems: "flex-start" }}
                    >
                      <div
                        style={{
                          width: "2.4rem",
                          height: "2.4rem",
                          marginRight: "1rem",
                          display: "flex",
                          alignItems: "flex-start",
                        }}
                      >
                        <NoteIcon />
                      </div>
                      <Paragraph
                        color={vars.colors.grey50}
                        fontSize={14}
                      >
                        {message.user.name} left an internal note{" "}
                        <Linkify options={{ target: "_blank" }}>
                          <b>{message.message}</b>
                        </Linkify>
                        &nbsp;&nbsp;&nbsp;
                        <Styled.Date>{resolveCreationTimeString(message.created)}</Styled.Date>
                      </Paragraph>
                    </Styled.SystemMessage>
                  );
                }
                return (
                  message.user.name !== "Shake AI" && (
                    <ChatMessage
                      key={message.id}
                      user={message.user}
                      isRead={message.is_read}
                      messageContent={message.message}
                      messageTimestamp={resolveCreationTimeString(message.created)}
                      appUser={appUser}
                    />
                  )
                );
              case IssueMessageType.ACTIVITY:
                return (
                  <Styled.SystemMessage key={message.id}>
                    <div style={{ width: "2.4rem", height: "2.4rem", marginRight: "1rem" }}>
                      {getActivityIcon(message.activity_type, message.activity_description.replace(/"|'/g, ""))}
                    </div>
                    {getActivityText(
                      message.activity_type,
                      message.activity_description.replace(/"|'/g, ""),
                      message.created,
                      message.activity_url,
                    )}
                  </Styled.SystemMessage>
                );
            }
          })}
          <Styled.MessageInputWrap>
            <MessageInput
              autoFocus={false}
              key={undefined}
              initialValue={undefined}
              placeholder={inputPlaceholder}
              onMessageSend={() => 0}
              testId={identifiers["central.column.comment.input"]}
              icontestid={identifiers["central.column.comment.icon.send"]}
              sendButtonText={"Add note"}
              sendToUserButton={
                <Styled.ToggleWrap>
                  <Styled.ToggleLabel>Send to app user</Styled.ToggleLabel>
                  <Styled.Tooltip
                    text="Register your app users to chat with them"
                    disabled={true}
                    position="top"
                  >
                    <ToggleButton
                      checked={false}
                      onChange={() => 0}
                      testId={identifiers["central.column.comment.toggle.sendToUser"]}
                      disabled={true}
                    />
                  </Styled.Tooltip>
                </Styled.ToggleWrap>
              }
            />
          </Styled.MessageInputWrap>
        </Fragment>
      )}
    </Styled.MessageContainerWrap>
  );
}

const getActivityIcon = (activityType: ActivityType | CrashActivityType, message?: string) => {
  if (activityType === ActivityType.RULE) {
    return <Styled.RuleIcon />;
  }
  if (activityType === ActivityType.TITLE) {
    return <DescriptionIcon />;
  }
  if (activityType === ActivityType.TAG) return <Icon.TagsIcon />;
  if (activityType === ActivityType.JIRA) return <JiraIcon />;
  if (activityType === ActivityType.LINEAR) return <LinearIcon />;
  if (activityType === ActivityType.DUPLICATE) return <Icon.DuplicateIcon />;
  if (activityType === ActivityType.CLEAR_DUPLICATE) return <Icon.ClearDuplicateIcon />;

  if (!message) return;
  if (activityType === ActivityType.ASSIGNEE) {
    const userNameArray = message.trimStart().trimEnd().split(" ");
    const userName = `${
      userNameArray[userNameArray.length - 2] !== "to" ? userNameArray[userNameArray.length - 2] : ""
    } ${userNameArray[userNameArray.length - 1]}`;
    return (
      <div style={{ marginRight: "0.8rem" }}>
        <Styled.RoundedCanvas
          size="xsmall"
          letter={!userName.includes("Unassigned") ? getUserInitials(userName) : undefined}
          bgColor={userName.includes("Unassigned") ? vars.colors.grey100 : vars.colors.lavender}
        />
      </div>
    );
  }
  if (activityType === ActivityType.STATUS) {
    if (message.endsWith(TicketStatus.IN_PROGRESS)) return <Icon.InProgressIcon />;
    if (message.endsWith(TicketStatus.NEW)) return <Icon.StatusNewIcon />;
    if (message.endsWith(TicketStatus.CLOSED)) return <Icon.StatusDoneIcon />;
    if (message.endsWith(TicketStatus.LOCKED)) return <Icon.LockedIcon />;
  }
  if (activityType === ActivityType.PRIORITY) {
    if (message.endsWith(TicketPriority.HIGH)) return <Icon.PriorityHighIcon />;
    if (message.endsWith(TicketPriority.LOW)) return <Icon.PriorityLowIcon />;
    if (message.endsWith(TicketPriority.MEDIUM)) return <Icon.PriorityMediumIcon />;
  }
};

const getActivityText = (
  activityType: ActivityType | CrashActivityType,
  message: string,
  created: string,
  url?: string,
) => {
  if (activityType === ActivityType.RULE && url) {
    // eslint-disable-next-line
    const [first, ...rest] = message.split(" ");
    const name = rest.join(" ").split(" triggered");

    return (
      <Paragraph
        fontSize={14}
        color={vars.colors.grey50}
      >
        Rule{" "}
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
        >
          {name}
        </a>{" "}
        was triggered.&nbsp;&nbsp;&nbsp;
        <Styled.Date>{resolveCreationTimeString(created)}</Styled.Date>
      </Paragraph>
    );
  }
  return (
    <Fragment>
      <Paragraph
        color={vars.colors.grey50}
        fontSize={14}
      >
        {message}.&nbsp;&nbsp;&nbsp;
        <Styled.Date>{resolveCreationTimeString(created)}</Styled.Date>
      </Paragraph>
    </Fragment>
  );
};

const noRulesTriggered = () => {
  return (
    <Styled.SystemMessage>
      {getActivityIcon(ActivityType.RULE)}
      <Paragraph
        fontSize={14}
        color={vars.colors.grey50}
      >
        No <span>Rules</span> were triggered.
      </Paragraph>
    </Styled.SystemMessage>
  );
};
